/**
 * Somebody hailing you.
 *
 * A radio alert is a chat message from something that is not another player —
 * a station, a faction, an admin. It goes in the log like anything else, and it
 * *also* comes up on screen, because a line scrolling past in a corner panel is
 * not how you tell someone their ship is about to hit a planet.
 *
 * ── Why it does not block ───────────────────────────────────────────────────
 *
 * No dismiss button, no pause. Everything it has to say is also in the radio
 * log, so a player who missed it has not lost anything — and a modal that
 * stopped the ship every time control spoke would make the tutorial worse than
 * no tutorial. It shows, it holds, it goes.
 *
 * ── Newest wins ─────────────────────────────────────────────────────────────
 *
 * One box, showing the latest thing said. It used to be a queue that played out
 * in order, which sounds fairer and is worse: dwell is long enough to read at,
 * so a burst of traffic left you watching a line you had already acted on while
 * the one that mattered waited its turn behind it. Anything superseded is still
 * in the radio log, which is where you go to check what you missed.
 *
 * Two overlapping boxes would be neither of them read, so it is never stacked.
 */

import { useEffect, useRef, useState } from 'react';

import { HUD_BACKING, HUD_DIM, HUD_ORANGE, HUD_TEXT } from './FlightHud';
import { HUD_SCALE } from './hudScale';

export interface Alert {
  id: string;
  /** Who is speaking, as they sign on. */
  from: string;
  /**
   * Where they are speaking from — the station, the ship, the outpost.
   *
   * A name on its own says who but not what you are dealing with, and by the
   * time it matters you are being talked at by more than one thing at once.
   */
  source?: string;
  text: string;
  /** Their colour. Faction alerts come in the faction's own. */
  accent?: string;
  /** Whether the player is expected to answer in the radio. */
  awaitsReply?: boolean;
}

/**
 * How long a line stays up, in milliseconds.
 *
 * Scaled to its length rather than fixed — a two-line instruction and a
 * three-word acknowledgement do not want the same dwell, and a fixed timer is
 * always wrong for one of them.
 */
/*
 * Read speed, roughly. Comfortable prose is around 200 words a minute — call it
 * eighteen characters a second, or 55ms each. These sit well clear of that,
 * because the reader is also flying a ship: the line is competing with the
 * thing it is describing, and it has to survive being glanced away from and
 * come back to.
 */
const DWELL_PER_CHARACTER = 95;
const DWELL_MIN = 6000;
const DWELL_MAX = 26_000;

/** A prompt stays until it is answered. Timing out a question is just rude. */
function dwellFor(alert: Alert): number {
  if (alert.awaitsReply) return Infinity;
  return Math.min(DWELL_MAX, Math.max(DWELL_MIN, alert.text.length * DWELL_PER_CHARACTER));
}

export interface RadioAlertProps {
  /** The queue. Newest last. Items are shown once, in order. */
  alerts: readonly Alert[];
  /** Fired when one has had its time, so the caller can drop it. */
  onDone: (id: string) => void;
  /**
   * Which end of the screen to sit at.
   *
   * The two views crowd opposite edges: in flight the console is along the
   * bottom, so a hail belongs up top; docked, every station control is along
   * the top, so it belongs down at the bottom. Same panel either way — it is
   * the same voice, and it should not look like a different system because the
   * ship stopped moving.
   */
  placement?: 'top' | 'bottom';

  /**
   * The console's power, while it is going or coming back.
   *
   * A hail is an instrument like any other — it is the radio talking — so it
   * goes dark with the rest of the cockpit rather than floating lit over a dead
   * console. Absent in a berth, where nothing is ever switched off.
   */
  power?: React.CSSProperties | null;
}

export function RadioAlert({ alerts, onDone, placement = 'top', power }: RadioAlertProps) {
  // The last one in, not the first. See the note at the top of the file.
  const showing = alerts[alerts.length - 1] ?? null;
  const [entered, setEntered] = useState(false);

  /**
   * The callback, held still.
   *
   * It used to be a dependency of the timer effect, and the caller passes an
   * inline arrow — so it was a new function on every render, and the flight HUD
   * renders every frame. The effect tore down and rebuilt sixty times a second:
   * the entry transform was reset each time, which is what made the box appear
   * to shake, and the dwell timer was cancelled long before it could fire, so
   * the first line went up and stayed there and nothing behind it in the queue
   * was ever seen.
   *
   * A ref because the effect wants the *latest* callback without being torn
   * down when it changes. The timer should restart when the line changes, and
   * for no other reason.
   */
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    if (!showing) return;

    setEntered(false);
    const raised = window.setTimeout(() => setEntered(true), 20);

    const dwell = dwellFor(showing);
    const id = showing.id;
    const expire = dwell === Infinity ? null : window.setTimeout(() => done.current(id), dwell);

    return () => {
      window.clearTimeout(raised);
      if (expire !== null) window.clearTimeout(expire);
    };
  }, [showing?.id]);

  if (!showing) return null;

  const accent = showing.accent ?? HUD_ORANGE;

  return (
    <div
      style={{
        ...power,
        position: 'absolute',
        // Clear of whichever edge the controls are crowding — see `placement`.
        ...(placement === 'top' ? { top: 92 } : { bottom: 92 }),
        left: '50%',
        /*
         * Four transforms in one, and the order is the whole of it.
         *
         * `translate(-50%, …)` is outermost so it still moves by half the
         * *unscaled* width, which is what `left: 50%` is expecting; the HUD
         * scale applies inside that; and the tube's collapse applies inside
         * both, so the panel squashes about its own middle rather than being
         * dragged across the screen as it goes.
         *
         * The vertical half is the entry: it rises into place from the edge it
         * is anchored to, so the movement always reads as the panel arriving
         * rather than sliding past.
         */
        transformOrigin: placement === 'top' ? 'top center' : 'bottom center',
        transform: `translate(-50%, ${
          entered ? '0' : placement === 'top' ? '-14px' : '14px'
        }) scale(${HUD_SCALE}) ${power?.transform ?? ''}`,
        opacity: Math.min(entered ? 1 : 0, typeof power?.opacity === 'number' ? power.opacity : 1),
        /*
         * The power's transition wins while there is one.
         *
         * `powerStyle` deliberately gives the flicker no transition at all and
         * the collapse a fast one — smoothing either is exactly what stops it
         * reading as a failure. This panel's own 180ms ease is for arriving, and
         * it would have averaged the flicker into a gentle dim.
         */
        transition:
          power?.transition ??
          'opacity 180ms linear, transform 180ms cubic-bezier(0.2, 0.9, 0.3, 1)',
        width: 'min(560px, 62vw)',
        background: HUD_BACKING,
        border: `1px solid ${accent}`,
        boxShadow: `0 0 26px ${accent}33`,
        color: HUD_TEXT,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '6px 12px',
          borderBottom: `1px solid ${accent}55`,
          color: accent,
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
        }}
      >
        {/* A lit pip, because a radio that is transmitting should look like it
            is transmitting. */}
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: accent,
            boxShadow: `0 0 8px ${accent}`,
            flexShrink: 0,
          }}
        />
        {showing.from}
        {showing.source && (
          <>
            {/* Doubled rule as a separator: it reads as a channel identifier
                rather than as punctuation in a sentence. */}
            <span style={{ opacity: 0.45 }}>||</span>
            <span style={{ opacity: 0.8 }}>{showing.source}</span>
          </>
        )}

        {showing.awaitsReply && (
          <span style={{ marginLeft: 'auto', color: HUD_DIM, letterSpacing: 1 }}>
            answer in the radio
          </span>
        )}
      </div>

      <div style={{ padding: '11px 14px', fontSize: 13.5, lineHeight: 1.5 }}>{showing.text}</div>
    </div>
  );
}
